/** Smart Waters brand emblem, product lockup, and theme toggle primitives. */
import { Moon, Sun } from "lucide-react";
import { type ReactElement, type SVGProps, useEffect, useId, useState } from "react";
import { useTranslation } from "react-i18next";

import { cn } from "@/shared/lib/format";
import { Button } from "@/shared/ui/button";

export const THEME_STORAGE_KEY = "mms.theme";
export type ThemeMode = "light" | "dark";

/** Read persisted theme mode (`light` by default). */
export function getInitialTheme(): ThemeMode {
  try {
    return window.localStorage.getItem(THEME_STORAGE_KEY) === "dark" ? "dark" : "light";
  } catch {
    return "light";
  }
}

/** Apply theme mode to `document.documentElement` and persist it. */
export function applyTheme(theme: ThemeMode): void {
  document.documentElement.classList.toggle("dark", theme === "dark");
  try {
    window.localStorage.setItem(THEME_STORAGE_KEY, theme);
  } catch {
    // Ignore storage quota / privacy mode errors.
  }
}

/** Toggle button switching between Smart Waters all-light and all-dark themes. */
export function ThemeToggle(): ReactElement {
  const { t } = useTranslation();
  const [theme, setTheme] = useState<ThemeMode>(() => {
    const initial = getInitialTheme();
    if (typeof document !== "undefined") {
      document.documentElement.classList.toggle("dark", initial === "dark");
    }
    return initial;
  });

  useEffect(() => {
    applyTheme(theme);
  }, [theme]);

  const next: ThemeMode = theme === "dark" ? "light" : "dark";
  const label = theme === "dark" ? t("app.lightMode") : t("app.darkMode");

  return (
    <Button
      variant="ghost"
      size="icon"
      aria-label={label}
      title={label}
      onClick={() => setTheme(next)}
    >
      {theme === "dark" ? (
        <Sun className="h-4 w-4 text-brand" aria-hidden="true" />
      ) : (
        <Moon className="h-4 w-4" aria-hidden="true" />
      )}
    </Button>
  );
}

/** Smart Waters 3-wave inverted triangle emblem. */
export function SmartWatersMark({ className, ...props }: SVGProps<SVGSVGElement>): ReactElement {
  const clipId = useId();
  return (
    <svg
      viewBox="0 0 32 32"
      aria-hidden="true"
      className={cn("size-6 shrink-0", className)}
      {...props}
    >
      <defs>
        <clipPath id={clipId}>
          <polygon points="2,3 30,3 16,29" />
        </clipPath>
      </defs>
      <g clipPath={`url(#${clipId})`}>
        <rect x="0" y="0" width="32" height="32" fill="#32BFB5" />
        <path d="M0,15 C7,16 10,15 15,11 C20,7 26,7 32,10 L32,32 L0,32 Z" fill="#92D5D8" />
        <path d="M0,16.5 C8,17.5 16,15 24,18.5 C28,20 30,21 32,22 L32,32 L0,32 Z" fill="#2D5275" />
        <path d="M0,17 C6,17.5 10,20 14,24.5 C17,28 21,30 26,32 L0,32 Z" fill="#0F1032" />
      </g>
    </svg>
  );
}
